import { VenueDetailsBySlugResponse, useAuth, useI18N } from "common/hooks";
import AccessibilitySpaceCard from "@/components/common/AccessibilitySpaceCard";
import { Helmet } from "react-helmet";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { bucketUrlPrefix } from "common/config";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  MagnifyingGlass,
  CaretDown,
  XCircle,
  House,
  SidebarSimple,
} from "phosphor-react";
import { TextSearch } from "lucide-react";
import { ComplexCategoriesData, TaggingCategoryData } from "common/types";
import SpaceAccessibilityDetails from "./SpaceAccessibilityDetails";
import NoSearchResults from "@/assets/images/noresults.svg";
import AccessibilityPhotosTab from "./AccessibilityPhotosTab";
import AccessibilityAlertsTab from "./AccessibilityAlertsTab";
import AccessibilityReviewsTab from "./AccessibilityReviewsTab";
import CategorySidebar from "./CategorySidebar";
import FullImageViewer from "@/components/FullImageViewer";
import FlagVenuePhoto from "@/features/flag-report/FlagVenuePhoto";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import AnalyticsService from "@/services/analytics";
import { Tooltip } from "react-tooltip";
import Checkbox from "@/components/common/Checkbox";

type AccessibilityGuideContentProps = {
  mode: "accessibility-guide" | "accessibility-guide-space";
  onClose: () => void;
  venueDetailsResponse: VenueDetailsBySlugResponse | undefined;
  onRefresh: () => void;
  cid?: string;
  tid?: string;
  venueSlug: string;
  initialSpaceId?: string;
  initialCategoryName?: string; // Optional since we can find it from spaceId
};

// Helper function to highlight matching words in text
function highlightMatches(text: string, searchTerm: string) {
  if (!searchTerm.trim()) {
    return <>{text}</>;
  }

  // Split search term into individual words
  const searchWords = searchTerm
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 0);

  // Create a regex pattern to match any of the search words (case-insensitive)
  const pattern = new RegExp(
    `(${searchWords.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
    "gi",
  );

  // Split text by matches and wrap matches in a span with highlight styling
  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, index) => {
        // Check if this part matches any search word
        const isMatch = searchWords.some(
          (word) => part.toLowerCase() === word.toLowerCase(),
        );
        if (isMatch) {
          return (
            <mark
              key={index}
              className="bg-yellow-200 text-fg-neutral-strong font-medium rounded px-0.5"
            >
              {part}
            </mark>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
}

function AccessibilityGuideContent({
  venueDetailsResponse: data,
  mode,
  onRefresh,
  onClose,
  venueSlug,
  initialSpaceId,
}: AccessibilityGuideContentProps) {
  const i18n = useI18N();
  const navigate = useNavigate();

  // Determine event name based on mode
  const isSpaceGuide = mode === "accessibility-guide-space";
  const getEventName = (baseName: string) => {
    if (isSpaceGuide) {
      // Map accessibility guide events to space guide events
      const eventMap: Record<string, string> = {
        "accessibilityGuideBackToVenueClicked": "accessibilitySpaceGuideBackToVenueClicked",
        "accessibilityGuideTabClicked": "accessibilitySpaceGuideTabClicked",
        "accessibilityGuidePhotosTabClicked": "accessibilitySpaceGuideTabClicked",
        "accessibilityGuideAlertsTabClicked": "accessibilitySpaceGuideTabClicked",
        "accessibilityGuideReviewsTabClicked": "accessibilitySpaceGuideTabClicked",
        "accessibilityGuideBackToListClicked": "accessibilitySpaceGuideBackToListClicked",
        "accessibilityGuideFilterAccessibilityOpened": "accessibilitySpaceGuideFilterAccessibilityOpened",
        "accessibilityGuideFilterAccessibilityClosed": "accessibilitySpaceGuideFilterAccessibilityClosed",
        "accessibilityGuideFilterAccessibilitySelected": "accessibilitySpaceGuideFilterAccessibilitySelected",
        "accessibilityGuideFilterFloorOpened": "accessibilitySpaceGuideFilterFloorOpened",
        "accessibilityGuideFilterFloorClosed": "accessibilitySpaceGuideFilterFloorClosed",
        "accessibilityGuideFilterFloorSelected": "accessibilitySpaceGuideFilterFloorSelected",
        "accessibilityGuideSidebarToggled": "accessibilitySpaceGuideSidebarToggled",
        "accessibilityGuideSpaceImageClicked": "accessibilitySpaceGuideSpaceImageClicked",
        "accessibilityGuideSpaceClicked": "accessibilitySpaceGuideSpaceClicked",
        "flagPhotoReportClose": "accessibilitySpaceGuideFlagClosed",
        "flagPhotoReportOpen": "accessibilitySpaceGuideFlagOpened",
      };
      return eventMap[baseName] || baseName;
    }
    return baseName;
  };

  // Get current route parameters to detect space ID from URL
  // This works regardless of which route (parent or child) is matched
  const currentParams = useParams({ strict: false });
  let currentSpaceId = (currentParams as any)?.spaceId || initialSpaceId;

  // Fallback: extract space ID from current URL if params don't work
  if (!currentSpaceId) {
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split("/");
    // URL format: /accessibility-guide-space/{venueId}/{spaceId}
    if (pathParts.length >= 4 && pathParts[1] === "accessibility-guide-space") {
      currentSpaceId = pathParts[3];
    }
  }
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
    new Set(),
  );
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedSpace, setSelectedSpace] = useState<{
    space: any;
    categoryName: string;
  } | null>(null);
  const [showSidebar, setShowSidebar] = useState(() => {
    // Initialize showSidebar based on screen size
    if (typeof window !== 'undefined') {
      // Default to true on desktop (lg breakpoint is 1024px), false on mobile
      return window.innerWidth >= 1024;
    }
    return true;
  });
  const [selectedAccessibilityFilters, setSelectedAccessibilityFilters] =
    useState<string[]>([]);
  const [selectedFloorFilters, setSelectedFloorFilters] = useState<string[]>(
    [],
  );
  const [showAccessibilityPanel, setShowAccessibilityPanel] = useState(false);
  const [showFloorPanel, setShowFloorPanel] = useState(false);
  const [accessibilityPanelPos, setAccessibilityPanelPos] = useState({ top: 0, right: 0 });
  const [floorPanelPos, setFloorPanelPos] = useState({ top: 0, right: 0 });
  const [accessibilitySearchTerm, setAccessibilitySearchTerm] = useState("");
  const [floorSearchTerm, setFloorSearchTerm] = useState("");
  const [fullImageView, setFullImageView] = useState(false);
  const [currentSpaceImages, setCurrentSpaceImages] = useState<any[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<
    "accessibility" | "photos" | "alerts" | "reviews"
  >("accessibility");

  // ARIA-live announcement state
  const [liveAnnouncement, setLiveAnnouncement] = useState("");

  // Flag photo state
  const [isFlagVenuePhotoOpen, setIsFlagVenuePhotoOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<any>(null);

  // Auth hooks
  const { user, isLoggedIn } = useAuth();
  const { showModal } = useAuthModals();

  // Refs for focus management in filter panels
  const accessibilityFilterButtonRef = useRef<HTMLButtonElement>(null);
  const accessibilitySearchInputRef = useRef<HTMLInputElement>(null);
  const floorFilterButtonRef = useRef<HTMLButtonElement>(null);
  const floorSearchInputRef = useRef<HTMLInputElement>(null);

  // Refs for scroll spy functionality
  const categorySectionRefs = useRef(new Map<string, HTMLElement>());
  const isProgrammaticScroll = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Handle responsive sidebar state on window resize
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;
      // Only auto-adjust on resize if transitioning between mobile and desktop
      if (isDesktop && !showSidebar) {
        // User resized to desktop, open sidebar by default
        setShowSidebar(true);
      } else if (!isDesktop && showSidebar) {
        // User resized to mobile, close sidebar by default
        setShowSidebar(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showSidebar]);

  // Handle ESC key to close sidebar on mobile
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showSidebar && window.innerWidth < 1024) {
        setShowSidebar(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showSidebar]);

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (showSidebar && typeof window !== 'undefined' && window.innerWidth < 1024) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showSidebar]);

  // Function to update URL with space parameters
  const updateSpaceUrl = (spaceId?: string | number) => {
    if (spaceId !== undefined && spaceId !== null) {
      // Convert to string and ensure it's clean without quotes
      const cleanSpaceId = String(spaceId).replace(/['"]/g, "");
      navigate({
        to: `/accessibility-guide-space/${venueSlug}/${cleanSpaceId}`,
        replace: false, // Don't replace - add to history for proper back button behavior
      });
    } else {
      // Navigate back to the main accessibility guide page (no space selected)
      navigate({
        to: `/accessibility-guide/${venueSlug}`,
        replace: false,
      });
    }
  };

  const reviewTags = useMemo(
    () => data?.review_tags || [],
    [data?.review_tags],
  );

  // Accessibility filter options from API data - use accessibility_filters.allFilterTags
  const accessibilityFilterOptions = useMemo(() => {
    if (!data?.accessibility_filters?.allFilterTags) {
      return [];
    }

    // Convert API data to the format expected by the component
    return data.accessibility_filters.allFilterTags
      .map((tag) => ({
        id: tag.tag_id.toString(),
        title: tag.tag_name,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [data?.accessibility_filters]);

  // Floor filter options from API data
  const floorFilterOptions = useMemo(
    () => data?.floorTypeOptions || [],
    [data?.floorTypeOptions],
  );

  // Filtered accessibility options based on search
  const filteredAccessibilityOptions = useMemo(() => {
    if (!accessibilitySearchTerm) return accessibilityFilterOptions;

    // Split search term into individual words and filter out empty strings
    const searchWords = accessibilitySearchTerm
      .toLowerCase()
      .split(/\s+/)
      .filter((word) => word.length > 0);

    return accessibilityFilterOptions.filter((option) => {
      const titleLower = option.title.toLowerCase();
      // Check if all search words exist in the title (in any order)
      return searchWords.every((word) => titleLower.includes(word));
    });
  }, [accessibilityFilterOptions, accessibilitySearchTerm]);

  // Filtered floor options based on search
  const filteredFloorOptions = useMemo(() => {
    if (!floorSearchTerm) return floorFilterOptions;

    // Split search term into individual words and filter out empty strings
    const searchWords = floorSearchTerm
      .toLowerCase()
      .split(/\s+/)
      .filter((word) => word.length > 0);

    return floorFilterOptions.filter((option) => {
      const titleLower = option.title.toLowerCase();
      // Check if all search words exist in the title (in any order)
      return searchWords.every((word) => titleLower.includes(word));
    });
  }, [floorFilterOptions, floorSearchTerm]);

  // Handle opening full image viewer for space
  const handleOpenSpaceImageViewer = (spaceItem: any) => {
    // Find the space data in the API response using the correct path
    let spacePhotos: any[] = [];

    // Navigate through the data structure: review_tags -> categoryOptions -> pageChildrenCategories -> levelOptions
    if (data?.review_tags && Array.isArray(data.review_tags)) {
      data.review_tags.forEach((category: any) => {
        if (category.categoryOptions) {
          category.categoryOptions.forEach((option: any) => {
            if (option.pageChildrenCategories) {
              option.pageChildrenCategories.forEach((subCategory: any) => {
                if (subCategory.levelOptions) {
                  subCategory.levelOptions.forEach((space: any) => {
                    // Match the space by ID or level name
                    if (
                      space.id === spaceItem.id ||
                      space.level === spaceItem.level
                    ) {
                      const filterOptions = space.level_tags_filter_options;

                      if (filterOptions) {
                        // Get photos from levelPhotos
                        if (
                          filterOptions.levelPhotos &&
                          Array.isArray(filterOptions.levelPhotos)
                        ) {
                          spacePhotos = filterOptions.levelPhotos.map(
                            (photo: any) => ({
                              src: bucketUrlPrefix + photo.path,
                              alt:
                                photo.alt_text || space.level || i18n.get("spaceImage"),
                              tag_name: space.level || i18n.get("unknownSpace"),
                              category_name: `${category.name} / ${subCategory.name}`,
                              created_at: photo.created_at,
                              photo_type: photo.photo_type,
                              review_photos_id: photo.review_photos_id,
                              width: photo.width,
                              height: photo.height,
                              sort_order: photo.sort_order,
                              qtags:
                                filterOptions.questionOptions?.flatMap(
                                  (question: any) =>
                                    question.tagOptions?.map((tag: any) => ({
                                      tagId: tag.optionTitleId,
                                      tagName: tag.optionTitle,
                                      categoryDisplaySectionId:
                                        question.category_display_section_id?.toString() ||
                                        "accessibility-features",
                                    })),
                                ) || [],
                              tags: [
                                {
                                  tagName: space.level || i18n.get("unknownSpace"),
                                  categoryDisplaySectionId:
                                    subCategory.id?.toString() || "",
                                },
                              ],
                              displaySections: (() => {
                                const sections: Record<string, string> = {};
                                filterOptions.questionOptions?.forEach(
                                  (question: any) => {
                                    if (
                                      question.category_display_section_id &&
                                      question.category_display_section_name
                                    ) {
                                      sections[
                                        question.category_display_section_id.toString()
                                      ] =
                                        question.category_display_section_name;
                                    }
                                  },
                                );
                                if (Object.keys(sections).length === 0) {
                                  sections["accessibility-features"] =
                                    i18n.get("accessibilityFeatures");
                                }
                                return sections;
                              })(),
                              venueId: data?.details?.id,
                              venueName: data?.details?.name,
                              venueAddress: data?.details?.address,
                              user_roles: {
                                isGeneralUser: false,
                              },
                            }),
                          );
                        }
                      }
                    }
                  });
                }
              });
            }
          });
        }
      });
    }

    if (spacePhotos.length > 0) {
      setCurrentSpaceImages(spacePhotos);
      setCurrentImageIndex(0);
      setFullImageView(true);
      // Remove focus from the button to prevent blue border after closing modal
      (document.activeElement as HTMLElement)?.blur();
    } else if (spaceItem.level_main_image_thumb || spaceItem.level_main_image) {
      // Fallback: show just the main image if no levelPhotos available
      const mainImageSrc =
        spaceItem.level_main_image || spaceItem.level_main_image_thumb;
      const fallbackImage = [
        {
          src: bucketUrlPrefix + mainImageSrc,
          alt: spaceItem.level || i18n.get("spaceImage"),
          tag_name: spaceItem.level || i18n.get("unknownSpace"),
          category_name: i18n.get("unknownCategory"),
          created_at: new Date().toISOString(),
          photo_type: "main",
          review_photos_id: null,
          width: 800,
          height: 600,
          sort_order: 1,
          qtags: [],
          tags: [
            {
              tagName: spaceItem.level || i18n.get("unknownSpace"),
              categoryDisplaySectionId: spaceItem.id?.toString() || "",
            },
          ],
          displaySections: {
            "accessibility-features": i18n.get("accessibilityFeatures"),
          },
          venueId: data?.details?.id,
          venueName: data?.details?.name,
          venueAddress: data?.details?.address,
          user_roles: {
            isGeneralUser: false,
          },
        },
      ];

      setCurrentSpaceImages(fallbackImage);
      setCurrentImageIndex(0);
      setFullImageView(true);
      // Remove focus from the button to prevent blue border after closing modal
      (document.activeElement as HTMLElement)?.blur();
    }
  };

  const handleFlagVenuePhotoClose = () => {
    const eventName = getEventName("flagPhotoReportClose");
    AnalyticsService.trackGAEventHandler(eventName);
    AnalyticsService.trackMx(eventName, {});
    setIsFlagVenuePhotoOpen(false);
  };

  const handleOpenFlagTracking = () => {
    const eventName = getEventName("flagPhotoReportOpen");
    AnalyticsService.trackGAEventHandler(eventName);
    AnalyticsService.trackMx(eventName, {});
  };

  const handleFlagPhoto = (image: any) => {
    handleOpenFlagTracking();

    if (!user || !isLoggedIn) {
      showModal(AuthModalScreen.EnterEmail);
      return;
    }
    setSelectedImage(image);
    setIsFlagVenuePhotoOpen(true);
  };

  // Collect all spaces and filter them based on search/filters
  const { filteredSpaces, filteredReviewTags, allSpaces } = useMemo(() => {
    // Collect all spaces from all categories
    const allSpacesWithCategories: Array<{
      space: any;
      categoryName: string;
      categoryId: number;
      parentCategoryName: string;
    }> = [];

    // Function to recursively collect spaces from all categories
    const collectSpaces = (
      categories: (ComplexCategoriesData | TaggingCategoryData)[],
      parentCategoryName?: string,
    ) => {
      categories.forEach((category) => {
        const currentParentName = parentCategoryName ?? category.name;
        if (category.categoryOptions) {
          category.categoryOptions.forEach((option) => {
            if (option.pageChildrenCategories) {
              option.pageChildrenCategories.forEach((subCategory) => {
                if (
                  subCategory.levelOptions &&
                  subCategory.levelOptions.length > 0
                ) {
                  subCategory.levelOptions.forEach((space) => {
                    const spaceWithIds = {
                      ...space,
                      review_id: data?.details?.business?.id?.toString(),
                      review_level_id: space.level_review_id?.toString(),
                    };
                    allSpacesWithCategories.push({
                      space: spaceWithIds,
                      categoryName: subCategory.name,
                      categoryId: subCategory.id,
                      parentCategoryName: currentParentName,
                    });
                  });
                }

                // Recursively collect from deeper levels
                if (subCategory.categoryOptions) {
                  collectSpaces([subCategory], currentParentName);
                }
              });
            }
          });
        }
      });
    };

    collectSpaces(reviewTags);

    // Filter spaces based on search term, floor filters, and accessibility filters
    const filteredSpaces = allSpacesWithCategories.filter((item) => {
      // Search term filter
      const matchesSearch =
        !searchTerm ||
        item.space.level.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.categoryName.toLowerCase().includes(searchTerm.toLowerCase());

      // Floor filter - match by floor title from floorTypeOptions
      const matchesFloor =
        selectedFloorFilters.length === 0 ||
        selectedFloorFilters.some((filterId) => {
          const selectedFloorOption = floorFilterOptions.find(
            (floor) => floor.id.toString() === filterId,
          );
          if (!selectedFloorOption) return false;
          if (
            item.space.business_floors &&
            item.space.business_floors.length > 0
          ) {
            return item.space.business_floors.some(
              (f) => f.floor_name === selectedFloorOption.title,
            );
          }
          return item.space.business_floor_name === selectedFloorOption.title;
        });

      // Accessibility filter - check if space has any of the selected accessibility tags
      const matchesAccessibility =
        selectedAccessibilityFilters.length === 0 ||
        (item.space.level_option_titles &&
          Array.isArray(item.space.level_option_titles) &&
          selectedAccessibilityFilters.some((filterId) => {
            const selectedAccessibilityOption = accessibilityFilterOptions.find(
              (option) => option.id === filterId,
            );
            return (
              selectedAccessibilityOption &&
              item.space.level_option_titles.includes(
                selectedAccessibilityOption.title,
              )
            );
          }));

      // Only show spaces that have accessibility info or photos
      const hasContent =
        (item.space.level_option_titles &&
          Array.isArray(item.space.level_option_titles) &&
          item.space.level_option_titles.length > 0) ||
        (item.space.level_photo_count && item.space.level_photo_count > 0);

      return matchesSearch && matchesFloor && matchesAccessibility && hasContent;
    });

    // Get unique category IDs that have visible spaces
    const visibleCategoryIds = new Set(
      filteredSpaces.map((item) => item.categoryId),
    );

    // Filter categories to only show those with visible spaces
    const filteredReviewTags = reviewTags
      .map((category) => {
        const filteredCategory = { ...category };

        if (filteredCategory.categoryOptions) {
          filteredCategory.categoryOptions = filteredCategory.categoryOptions
            .map((option) => {
              const filteredOption = { ...option };

              if (filteredOption.pageChildrenCategories) {
                filteredOption.pageChildrenCategories =
                  filteredOption.pageChildrenCategories.filter(
                    (subCategory) => {
                      // Check if this subcategory has any visible spaces
                      const hasVisibleSpaces = visibleCategoryIds.has(
                        subCategory.id,
                      );

                      // Also check nested categories
                      if (!hasVisibleSpaces && subCategory.categoryOptions) {
                        const hasNestedVisibleSpaces =
                          subCategory.categoryOptions.some((nestedOption) =>
                            nestedOption.pageChildrenCategories?.some(
                              (nestedSubCategory) =>
                                visibleCategoryIds.has(nestedSubCategory.id),
                            ),
                          );
                        return hasNestedVisibleSpaces;
                      }

                      return hasVisibleSpaces;
                    },
                  );
              }

              return filteredOption;
            })
            .filter(
              (option) =>
                // Only keep options that have visible subcategories
                option.pageChildrenCategories &&
                option.pageChildrenCategories.length > 0,
            );
        }

        return filteredCategory;
      })
      .filter(
        (category) =>
          // Only keep categories that have visible options
          category.categoryOptions && category.categoryOptions.length > 0,
      );

    return {
      filteredSpaces,
      filteredReviewTags,
      allSpaces: allSpacesWithCategories,
    };
  }, [
    reviewTags,
    searchTerm,
    selectedFloorFilters,
    selectedAccessibilityFilters,
    floorFilterOptions,
    accessibilityFilterOptions,
    data?.details?.business?.id,
  ]);

  // Helper function to find category ID for a given space
  const findCategoryIdForSpace = useCallback(
    (spaceCategoryName: string): number | null => {
      for (const category of filteredReviewTags) {
        if (category.categoryOptions) {
          for (const option of category.categoryOptions) {
            if (option.pageChildrenCategories) {
              for (const subCategory of option.pageChildrenCategories) {
                if (subCategory.name === spaceCategoryName) {
                  return subCategory.id;
                }
              }
            }
          }
        }
      }
      return null;
    },
    [filteredReviewTags],
  );

  // Helper function to find all parent category IDs that need to be expanded
  const findParentCategoriesToExpand = useCallback(
    (spaceCategoryName: string): number[] => {
      const parentIds: number[] = [];

      for (const category of filteredReviewTags) {
        if (category.categoryOptions) {
          for (const option of category.categoryOptions) {
            if (option.pageChildrenCategories) {
              for (const subCategory of option.pageChildrenCategories) {
                if (subCategory.name === spaceCategoryName) {
                  // Add the top-level category ID
                  parentIds.push(category.id);

                  // Add the option ID if it exists (for nested structures)
                  if (option.pageTitleId) {
                    parentIds.push(option.pageTitleId);
                  }

                  return parentIds;
                }
              }
            }
          }
        }
      }
      return parentIds;
    },
    [filteredReviewTags],
  );

  // Close filter panels when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest("[data-filter-panel]")) {
        setShowAccessibilityPanel(false);
        setShowFloorPanel(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close filter panels on scroll to keep fixed-position panels in sync with sticky button
  useEffect(() => {
    const handleScroll = () => {
      if (showAccessibilityPanel) {
        setShowAccessibilityPanel(false);
        setAccessibilitySearchTerm("");
      }
      if (showFloorPanel) {
        setShowFloorPanel(false);
        setFloorSearchTerm("");
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showAccessibilityPanel, showFloorPanel]);

  // Auto-expand all 1st level categories on initial page load (but not 2nd level)
  useEffect(() => {
    if (filteredReviewTags.length > 0 && expandedCategories.size === 0) {
      const allFirstLevelIds: number[] = [];

      filteredReviewTags.forEach((category) => {
        allFirstLevelIds.push(category.id);
      });

      setExpandedCategories(new Set(allFirstLevelIds));
    }
  }, [filteredReviewTags, expandedCategories.size]);

  // Sync selected space from URL (initial load and browser back/forward)
  useEffect(() => {
    if (!currentSpaceId || allSpaces.length === 0) return;

    const cleanSpaceId = String(currentSpaceId).replace(/['"]/g, "");
    const spaceIdAsNumber = parseInt(cleanSpaceId, 10);

    const targetSpace = allSpaces.find((item) => {
      const itemId = item.space.id;
      return itemId === spaceIdAsNumber || String(itemId) === cleanSpaceId;
    });

    if (!targetSpace) return;

    // Only update state when URL points to a different space to avoid redundant updates
    const currentId = selectedSpace?.space?.id;
    const targetId = targetSpace.space.id;
    const alreadySynced =
      currentId === targetId || String(currentId) === cleanSpaceId;
    if (alreadySynced) return;

    setSelectedSpace({
      space: targetSpace.space,
      categoryName: targetSpace.categoryName,
    });

    const categoryId = findCategoryIdForSpace(targetSpace.categoryName);
    if (categoryId) {
      setSelectedCategory(categoryId);
    }
  }, [currentSpaceId, allSpaces, selectedSpace, findCategoryIdForSpace]);

  // Scroll spy - auto-select category when scrolling through sections
  useEffect(() => {
    // Only enable scroll spy when viewing the space list (not individual space)
    if (selectedSpace || filteredSpaces.length === 0) return;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    let timeoutId: NodeJS.Timeout;

    const handleScroll = () => {
      // Don't update during programmatic scrolls
      if (isProgrammaticScroll.current) {
        return;
      }

      // Debounce scroll updates to prevent jitter
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // Find which category section has the most visible content in the viewport
        const containerRect = scrollContainer.getBoundingClientRect();
        const headerOffset = 20; // Small offset from top
        let mostVisibleCategory: string | null = null;
        let maxVisibleArea = 0;

        for (const [categoryName, ref] of categorySectionRefs.current) {
          if (ref) {
            const rect = ref.getBoundingClientRect();

            // Calculate how much of this section is visible in the scroll container
            const visibleTop = Math.max(rect.top, containerRect.top + headerOffset);
            const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
            const visibleArea = Math.max(0, visibleBottom - visibleTop);

            // Track which section has the most visible content
            if (visibleArea > maxVisibleArea) {
              maxVisibleArea = visibleArea;
              mostVisibleCategory = categoryName;
            }
          }
        }

        // Update selected category if we found a visible one
        if (mostVisibleCategory) {
          const categoryId = findCategoryIdForSpace(mostVisibleCategory);
          if (categoryId && categoryId !== selectedCategory) {
            setSelectedCategory(categoryId);

            // Auto-expand parent categories so the highlighted category is visible
            const parentIds = findParentCategoriesToExpand(mostVisibleCategory);
            if (parentIds.length > 0) {
              setExpandedCategories((prev) => {
                const newSet = new Set(prev);
                parentIds.forEach((id) => newSet.add(id));
                return newSet;
              });
            }
          }
        }
      }, 100); // 100ms debounce
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, [filteredSpaces, selectedSpace, selectedCategory, findCategoryIdForSpace, findParentCategoriesToExpand]);

  // Focus management for accessibility filter panel
  useEffect(() => {
    if (showAccessibilityPanel && accessibilitySearchInputRef.current) {
      // Focus the search input when the panel opens
      accessibilitySearchInputRef.current.focus();
    } else if (!showAccessibilityPanel && accessibilityFilterButtonRef.current) {
      // Restore focus to the toggle button when the panel closes
      accessibilityFilterButtonRef.current.focus();
    }
  }, [showAccessibilityPanel]);

  // Focus management for floor filter panel
  useEffect(() => {
    if (showFloorPanel && floorSearchInputRef.current) {
      // Focus the search input when the panel opens
      floorSearchInputRef.current.focus();
    } else if (!showFloorPanel && floorFilterButtonRef.current) {
      // Restore focus to the toggle button when the panel closes
      floorFilterButtonRef.current.focus();
    }
  }, [showFloorPanel]);

  // Announce filter results changes with debouncing
  useEffect(() => {
    // Don't announce on initial load when nothing is selected
    const hasActiveFilters = selectedAccessibilityFilters.length > 0 ||
      selectedFloorFilters.length > 0 ||
      selectedCategory !== null ||
      searchTerm.trim() !== '';

    if (!hasActiveFilters) {
      return;
    }

    // Debounce announcements
    const timeoutId = setTimeout(() => {
      const parts: string[] = [];

      if (selectedCategory) {
        // Find category name by ID
        const findCategoryName = (catId: number): string | null => {
          for (const category of filteredReviewTags) {
            if (category.id === catId) return category.name;
            if (category.categoryOptions) {
              for (const option of category.categoryOptions) {
                if (option.pageTitleId === catId) return option.pageTitle;
                if (option.pageChildrenCategories) {
                  for (const subCat of option.pageChildrenCategories) {
                    if (subCat.id === catId) return subCat.name;
                  }
                }
              }
            }
          }
          return null;
        };

        const categoryName = findCategoryName(selectedCategory);
        if (categoryName) {
          parts.push(`Filtered to ${categoryName}`);
        }
      }

      if (selectedAccessibilityFilters.length > 0) {
        parts.push(`${selectedAccessibilityFilters.length} accessibility ${selectedAccessibilityFilters.length === 1 ? 'filter' : 'filters'} applied`);
      }

      if (selectedFloorFilters.length > 0) {
        parts.push(`${selectedFloorFilters.length} floor ${selectedFloorFilters.length === 1 ? 'filter' : 'filters'} applied`);
      }

      if (searchTerm.trim()) {
        parts.push(`Searching for ${searchTerm}`);
      }

      parts.push(`Showing ${filteredSpaces.length} ${filteredSpaces.length === 1 ? 'space' : 'spaces'}`);

      setLiveAnnouncement(parts.join('. ') + '.');
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [selectedAccessibilityFilters, selectedFloorFilters, selectedCategory, searchTerm, filteredSpaces.length, filteredReviewTags]);

  // Detect if we're viewing a specific cohort based on the venueSlug parameter
  const currentCohort = useMemo(() => {
    if (!data?.details?.cohortsData || !venueSlug) return null;

    // Check if the venueSlug matches any cohort slug
    const cohort = data.details.cohortsData.find(
      (cohort) => cohort.slug === venueSlug,
    );
    return cohort || null;
  }, [data?.details?.cohortsData, venueSlug]);

  // Generate dynamic title based on cohort only (not individual spaces)
  const pageTitle = useMemo(() => {
    if (currentCohort) {
      // When viewing a specific cohort, use the cohort name as title
      return `${currentCohort.cohort_name} | ${data?.details?.name || i18n.get("venue")} | ${i18n.get("appName")}`;
    }
    // Default title when viewing the main accessibility guide or individual spaces
    return `${i18n.get("accessibilityGuide")} | ${data?.details?.name || i18n.get("venue")} | ${i18n.get("appName")}`;
  }, [currentCohort, data?.details?.name, i18n]);

  return (
    <div className="flex flex-col md:h-[calc(100vh-90px)] md:overflow-hidden">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      {/* Scrollbar styles for webkit browsers (Chrome, Safari, Edge) */}
      <style>{`
 .scrollbar-hide::-webkit-scrollbar {
 width: 8px;
 height: 8px;
 }
 .scrollbar-hide::-webkit-scrollbar-track {
 background: transparent;
 }
 .scrollbar-hide::-webkit-scrollbar-thumb {
 background: transparent;
 border-radius: 4px;
 }
 .scrollbar-hide:hover::-webkit-scrollbar-thumb {
 background: #CBD5E1;
 }
 .scrollbar-hide:hover::-webkit-scrollbar-thumb:hover {
 background: #94A3B8;
 }`}</style>

      {/* Breadcrumb Header - only visible in accessibility-guide mode  */}
      {mode === "accessibility-guide" && (
        <header className="self-stretch py-3 bg-surface-secondary border-b border-border-token-brand-minimal flex-shrink-0 w-full inline-flex flex-col justify-start items-center">
          <div className="max-w-[1440px] mx-auto w-full px-4 md:px-7  flex flex-col justify-start items-start">
            <nav
              aria-label="Breadcrumb"
              className="flex-1 flex justify-start items-center min-w-0"
            >
              <ol className="flex items-center gap-2 list-none m-0 p-0">
                {/* Venue Name with Home Icon */}
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      const eventName = getEventName("accessibilityGuideBackToVenueClicked");
                      AnalyticsService.trackGAEventHandler(eventName);
                      AnalyticsService.trackMx(eventName, {
                        VenueId: data?.details.business.id,
                        VenueName: data?.details.name,
                      });
                      onClose();
                    }}
                    className="min-w-8 min-h-8 bg-white/0 rounded-lg flex items-center gap-1.5 text-fg-neutral-strong hover:text-fg-brand-subtle transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    aria-label={i18n.get("backToVenueDetails")}
                  >
                    <House size={20} className="text-fg-neutral-subtle" weight="fill" />
                    <span className="text-center justify-center text-fg-neutral-strong label-m-medium">{data?.details?.name}</span>
                  </button>
                </li>
              </ol>
            </nav>
          </div>

          {/* Title Row */}
          <div className="max-w-[1440px] mx-auto w-full self-stretch px-4 md:px-7 flex flex-col justify-start items-start">
            <div className="self-stretch inline-flex justify-start items-center gap-3">
              <h1 className="flex-1 justify-center text-fg-neutral-strong title-l-bold">
                {currentCohort
                  ? currentCohort.cohort_name
                  : i18n.get("accessibilityGuide")}
              </h1>
            </div>
          </div>
        </header>
      )}

      {/* Tabs with white background and border */}
      {mode === "accessibility-guide" && (
        <div className="bg-bg-brand-secondary-default border-b border-b-primary-150 flex-shrink-0 max-w-[1440px] mx-auto w-full mt-1">
          <div className="px-7">
            <div
              className="flex gap-0 overflow-x-auto"
              role="tablist"
              aria-label="Accessibility guide sections"
              tabIndex={0}
              onKeyDown={(e) => {
                const tabs: Array<"accessibility" | "photos" | "alerts" | "reviews"> = ["accessibility", "photos", "alerts", "reviews"];
                const currentIndex = tabs.indexOf(activeTab);

                if (e.key === "ArrowRight") {
                  e.preventDefault();
                  const nextIndex = (currentIndex + 1) % tabs.length;
                  setActiveTab(tabs[nextIndex]);
                } else if (e.key === "ArrowLeft") {
                  e.preventDefault();
                  const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
                  setActiveTab(tabs[prevIndex]);
                }
              }}
            >
              <button
                role="tab"
                aria-selected={activeTab === "accessibility"}
                aria-controls="accessibility-tabpanel"
                id="accessibility-tab"
                tabIndex={activeTab === "accessibility" ? 0 : -1}
                onClick={() => {
                  setActiveTab("accessibility");
                  const eventName = getEventName("accessibilityGuideTabClicked");
                  AnalyticsService.trackGAEventHandler(eventName);
                  AnalyticsService.trackMx(eventName, {});
                }}
                className={`${activeTab === "accessibility"
                  ? "border-b-2 border-primary-500 text-fg-brand-subtle"
                  : "text-fg-neutral-strong"
                  } bg-transparent rounded-none px-3 md:px-4 py-2 title-s-semibold whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
              >
                {i18n.get("accessibility")}
              </button>
              <button
                role="tab"
                aria-selected={activeTab === "photos"}
                aria-controls="photos-tabpanel"
                id="photos-tab"
                tabIndex={activeTab === "photos" ? 0 : -1}
                onClick={() => {
                  setActiveTab("photos");
                  const eventName = getEventName("accessibilityGuidePhotosTabClicked");
                  AnalyticsService.trackGAEventHandler(eventName);
                  AnalyticsService.trackMx(eventName, {});
                }}
                className={`${activeTab === "photos"
                  ? "border-b-2 border-primary-500 text-fg-brand-subtle"
                  : "text-fg-neutral-strong"
                  } bg-transparent rounded-none px-3 md:px-4 py-2 title-s-semibold whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
              >
                {i18n.get("photos")}
              </button>
              <button
                role="tab"
                aria-selected={activeTab === "alerts"}
                aria-controls="alerts-tabpanel"
                id="alerts-tab"
                tabIndex={activeTab === "alerts" ? 0 : -1}
                onClick={() => {
                  setActiveTab("alerts");
                  const eventName = getEventName("accessibilityGuideAlertsTabClicked");
                  AnalyticsService.trackGAEventHandler(eventName);
                  AnalyticsService.trackMx(eventName, {});
                }}
                className={`${activeTab === "alerts"
                  ? "border-b-2 border-primary-500 text-fg-brand-subtle"
                  : "text-fg-neutral-strong"
                  } bg-transparent rounded-none px-3 md:px-4 py-2 title-s-semibold whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
              >
                {i18n.get("alerts")}
              </button>
              <button
                role="tab"
                aria-selected={activeTab === "reviews"}
                aria-controls="reviews-tabpanel"
                id="reviews-tab"
                tabIndex={activeTab === "reviews" ? 0 : -1}
                onClick={() => {
                  setActiveTab("reviews");
                  const eventName = getEventName("accessibilityGuideReviewsTabClicked");
                  AnalyticsService.trackGAEventHandler(eventName);
                  AnalyticsService.trackMx(eventName, {});
                }}
                className={`${activeTab === "reviews"
                  ? "border-b-2 border-primary-500 text-fg-brand-subtle"
                  : "text-fg-neutral-strong"
                  } bg-transparent rounded-none px-3 md:px-4 py-2 title-s-semibold whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
              >
                {i18n.get("reviews")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-h-0 max-w-[1440px] mx-auto w-full">
        {activeTab === "accessibility" && (
          <div
            role="tabpanel"
            id="accessibility-tabpanel"
            aria-labelledby="accessibility-tab"
            tabIndex={0}
            className="bg-bg-brand-secondary-default rounded-lg shadow-sm flex-1 flex flex-col min-h-0 md:overflow-hidden"
          >
            {/* Screen reader announcements for filter results */}
            <div
              className="sr-only"
              aria-live="polite"
              aria-atomic="true"
              role="status"
            >
              {liveAnnouncement}
            </div>

            {/* Skip Navigation Link for Screen Readers */}
            <a
              href="#main-content-area"
              onClick={(e) => {
                e.preventDefault();
                const mainContent = document.getElementById('main-content-area');
                if (mainContent) {
                  mainContent.focus();
                  mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-bg-brand-primary-default focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {i18n.get("skipNavigation")}
            </a>

            <div className="flex flex-col md:flex-row flex-1 min-h-0">
              {/* Backdrop for mobile drawer - only visible on mobile when sidebar is open */}
              {showSidebar && (
                <div
                  className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                  onClick={() => setShowSidebar(false)}
                  aria-hidden="true"
                />
              )}

              {/* Left Navigation Panel - Sidebar wrapper with transitions (Focus Group 1) */}
              <nav
                aria-label="Space categories navigation"
                className={`
                  lg:relative lg:overflow-hidden lg:h-full
                  ${showSidebar ? "lg:w-[350px] lg:flex-shrink-0" : "lg:w-0"}
                  lg:transition-all lg:duration-300
                `}
              >
                <div
                  className={`
                    fixed top-0 left-0 right-0 bottom-0 w-full z-50
                    transform transition-transform duration-300 ease-in-out
                    ${showSidebar ? "translate-x-0" : "-translate-x-full"}
                    lg:relative lg:inset-auto lg:w-full lg:h-full lg:transform-none lg:transition-none
                  `}
                  role="dialog"
                  aria-modal="true"
                  aria-label="Categories navigation sidebar"
                >


                  <CategorySidebar
                    venueDetailsResponse={data}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    expandedCategories={expandedCategories}
                    setExpandedCategories={setExpandedCategories}
                    showMobileMenu={showSidebar}
                    setShowMobileMenu={setShowSidebar}
                    searchPlaceholder={i18n.get("spaceName")}
                    filteredReviewTags={filteredReviewTags}
                    onCategorySelect={(categoryId, categoryName) => {
                      // If we're viewing a space, go back to space list first
                      if (selectedSpace) {
                        // Clear URL parameters first
                        updateSpaceUrl();

                        // Use setTimeout to ensure URL update happens first, then update state
                        setTimeout(() => {
                          setSelectedSpace(null);
                          setShowSidebar(false);

                          // Set the selected category and scroll to it
                          setSelectedCategory(categoryId);

                          // Scroll to the category section
                          setTimeout(() => {
                            isProgrammaticScroll.current = true;
                            const parentCategoryName = filteredSpaces.find(item => item.categoryId === categoryId)?.parentCategoryName ?? "";
                            const categoryElementId = `category-${parentCategoryName}-${categoryName}`.toLowerCase().replace(/[^a-z0-9]/g, "-");
                            const element =
                              document.getElementById(categoryElementId);
                            const scrollContainer =
                              element?.closest(".overflow-y-auto");

                            if (element && scrollContainer) {
                              const containerRect =
                                scrollContainer.getBoundingClientRect();
                              const elementRect = element.getBoundingClientRect();
                              const offset = 20; // Small offset from top

                              const scrollPosition =
                                scrollContainer.scrollTop +
                                (elementRect.top - containerRect.top) -
                                offset;

                              scrollContainer.scrollTo({
                                top: scrollPosition,
                                behavior: "smooth",
                              });
                            } else if (element) {
                              // Fallback to default scrollIntoView
                              element.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                                inline: "nearest",
                              });
                            }

                            // Reset flag after scroll completes
                            setTimeout(() => {
                              isProgrammaticScroll.current = false;
                            }, 1000);
                          }, 100);

                          // Clear selection after scrolling (only when coming back from space details)
                          setTimeout(() => {
                            setSelectedCategory(null);
                          }, 500);
                        }, 0);
                      } else {
                        // If we're already in the space list, just scroll to the category
                        // Don't override the selectedCategory since CategorySidebar already set it
                        setTimeout(() => {
                          isProgrammaticScroll.current = true;
                          const parentCategoryName = filteredSpaces.find(item => item.categoryId === categoryId)?.parentCategoryName ?? "";
                          const categoryElementId = `category-${parentCategoryName}-${categoryName}`.toLowerCase().replace(/[^a-z0-9]/g, "-");
                          const element =
                            document.getElementById(categoryElementId);
                          const scrollContainer =
                            element?.closest(".overflow-y-auto");

                          if (element && scrollContainer) {
                            const containerRect =
                              scrollContainer.getBoundingClientRect();
                            const elementRect = element.getBoundingClientRect();
                            const offset = 20; // Small offset from top

                            const scrollPosition =
                              scrollContainer.scrollTop +
                              (elementRect.top - containerRect.top) -
                              offset;

                            scrollContainer.scrollTo({
                              top: scrollPosition,
                              behavior: "smooth",
                            });
                          } else if (element) {
                            // Fallback to default scrollIntoView
                            element.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                              inline: "nearest",
                            });
                          }

                          // Reset flag after scroll completes
                          setTimeout(() => {
                            isProgrammaticScroll.current = false;
                          }, 1000);
                        }, 100);
                      }
                    }}
                    pageName={isSpaceGuide ? "Accessibility Space Guide" : "Accessibility Guide"}
                  />
                </div>
              </nav>

              {/* Right Content Area (Focus Group 2) */}
              <section
                id="main-content-area"
                aria-label="Accessibility spaces content"
                tabIndex={-1}
                className={`flex-1 flex flex-col min-h-0 focus:outline-none ${selectedSpace ? "flex" : showSidebar ? "hidden md:flex" : "flex"}`}
              >
                {/* Desktop Toggle Button - Always visible when viewing a space */}
                {selectedSpace && (
                  <>
                    {/* Breadcrumb Navigation */}
                    <div className="self-stretch px-7 inline-flex justify-start items-start gap-2.5 pt-2">
                      <nav
                        aria-label="Breadcrumb"
                        className="flex-1 flex justify-start items-center min-w-0"
                      >
                        <ol className="flex items-center gap-2 list-none m-0 p-0">
                          {/* Venue Name with Home Icon */}
                          <li>
                            <button
                              type="button"
                              onClick={onClose}
                              className="min-w-8 min-h-8 bg-white/0 rounded-lg flex items-center gap-1.5 text-fg-neutral-strong hover:text-fg-brand-subtle transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                              aria-label={i18n.get("backToVenueDetails")}
                            >
                              <House size={20} className="text-fg-neutral-subtle" weight="fill" />
                              <span className="text-center justify-center text-fg-neutral-strong label-m-medium">{data?.details?.name}</span>
                            </button>
                          </li>

                          {/* Separator */}
                          <li aria-hidden="true">
                            <span className="text-center justify-center text-fg-neutral-subtle label-m-medium">/</span>
                          </li>

                          <li>
                            <button
                              type="button"
                              onClick={() => {
                                // Track navigation back to list
                                const eventName = getEventName("accessibilityGuideBackToListClicked");
                                AnalyticsService.trackGAEventHandler(eventName);
                                AnalyticsService.trackMx(eventName, {});

                                // Clear URL parameters and go back to list
                                updateSpaceUrl();
                                setTimeout(() => {
                                  setSelectedSpace(null);
                                  setSelectedCategory(null);
                                  setShowSidebar(false);
                                }, 0);
                              }}
                              className="min-w-8 min-h-8 bg-white/0 rounded-lg flex items-center gap-1.5 text-fg-neutral-strong hover:text-fg-brand-subtle transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                              aria-label={i18n.get("backToFullAccessibilityGuide")}
                            >
                              <span className="text-center justify-center text-fg-neutral-strong label-m-medium" aria-current="page">
                                {currentCohort
                                  ? currentCohort.cohort_name
                                  : i18n.get("accessibilityGuide")}
                              </span>
                            </button>
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </>
                )}

                <div
                  className={`flex-1 scrollbar-hide hover:scrollbar-show ${selectedSpace ? "md:overflow-hidden" : "md:overflow-y-auto"}`}
                  style={{
                    scrollbarWidth: "thin",
                    scrollbarColor: "transparent transparent",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.scrollbarColor =
                      "#CBD5E1 transparent";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.scrollbarColor =
                      "transparent transparent";
                  }}
                >
                  {selectedSpace ? (
                    <SpaceAccessibilityDetails
                      venueDetailsResponse={data}
                      space={selectedSpace.space}
                      categoryName={selectedSpace.categoryName}
                      showSidebar={showSidebar}
                      setShowSidebar={setShowSidebar}
                      onBack={() => {
                        // Clear URL parameters first, then update state
                        updateSpaceUrl();

                        // Use setTimeout to ensure URL update happens first
                        setTimeout(() => {
                          setSelectedSpace(null);
                          setSelectedCategory(null); // Clear category highlighting when going back
                          setShowSidebar(false);
                        }, 0);
                      }}
                      onNavigateToSpace={(connectedSpaceId) => {
                        // Find the connected space in the allSpaces array
                        const connectedSpace = allSpaces.find(
                          (item) => item.space.id === connectedSpaceId,
                        );

                        if (connectedSpace) {
                          setSelectedSpace({
                            space: connectedSpace.space,
                            categoryName: connectedSpace.categoryName,
                          });

                          // Update URL with new space parameters
                          updateSpaceUrl(connectedSpace.space.id);
                        }
                      }}
                      onPreviousSpace={() => {
                        // Find current space index in filteredSpaces
                        const currentIndex = filteredSpaces.findIndex(
                          (item) => item.space.id === selectedSpace.space.id,
                        );
                        if (currentIndex > 0) {
                          const previousSpace =
                            filteredSpaces[currentIndex - 1];
                          const newSelectedSpace = {
                            space: previousSpace.space,
                            categoryName: previousSpace.categoryName,
                          };
                          setSelectedSpace(newSelectedSpace);

                          // Highlight the category associated with this space
                          const categoryId = findCategoryIdForSpace(
                            previousSpace.categoryName,
                          );
                          if (categoryId) {
                            setSelectedCategory(categoryId);
                          }

                          // Update URL with new space parameters
                          updateSpaceUrl(previousSpace.space.id);
                        }
                      }}
                      onNextSpace={() => {
                        // Find current space index in filteredSpaces
                        const currentIndex = filteredSpaces.findIndex(
                          (item) => item.space.id === selectedSpace.space.id,
                        );
                        if (currentIndex < filteredSpaces.length - 1) {
                          const nextSpace = filteredSpaces[currentIndex + 1];
                          const newSelectedSpace = {
                            space: nextSpace.space,
                            categoryName: nextSpace.categoryName,
                          };
                          setSelectedSpace(newSelectedSpace);

                          // Highlight the category associated with this space
                          const categoryId = findCategoryIdForSpace(
                            nextSpace.categoryName,
                          );
                          if (categoryId) {
                            setSelectedCategory(categoryId);
                          }

                          // Update URL with new space parameters
                          updateSpaceUrl(nextSpace.space.id);
                        }
                      }}
                      hasPreviousSpace={(() => {
                        const currentIndex = filteredSpaces.findIndex(
                          (item) => item.space.id === selectedSpace.space.id,
                        );
                        return currentIndex > 0;
                      })()}
                      hasNextSpace={(() => {
                        const currentIndex = filteredSpaces.findIndex(
                          (item) => item.space.id === selectedSpace.space.id,
                        );
                        return currentIndex < filteredSpaces.length - 1;
                      })()}
                      pageName={isSpaceGuide ? "Accessibility Space Guide" : "Space Accessibility Details"}
                    />
                  ) : (
                    <div className="flex flex-col h-full">

                      {/* Fixed Filters Section */}
                      <div className="flex-shrink-0 px-6 pt-3 sticky top-[77px] z-20 bg-bg-brand-secondary-default md:static">
                        {/* Header - Total Spaces Count with Filters */}
                        <div className="mb-3">
                          {/* Filter Buttons and Spaces Count */}
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            {/* Filters - first on mobile, second on desktop */}
                            <div className="flex items-center gap-3 relative order-1 md:order-2">

                              {/* Mobile Search Button - only visible on mobile */}
                              <button
                                onClick={() => setShowSidebar(true)}
                                className={`md:hidden flex items-center gap-2 px-3 py-2 label-m-medium border rounded-lg transition-colors ${searchTerm || showSidebar
                                  ? "bg-container-brand-minimal border-border-token-brand-minimal text-fg-brand-strong"
                                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                                  }`}
                                aria-label={i18n.get("searchSpacesByName")}
                              >
                                <TextSearch size={16} aria-hidden="true" />
                                {i18n.get("search")}
                                {searchTerm && (
                                  <span className="bg-bg-brand-primary-default text-white body-s-regular rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                                    1
                                  </span>
                                )}
                              </button>

                              {/* Accessibility Filter Button */}
                              <div className="relative" data-filter-panel>
                                <button
                                  ref={accessibilityFilterButtonRef}
                                  onClick={() => {
                                    const opening = !showAccessibilityPanel;
                                    setShowAccessibilityPanel(opening);
                                    if (opening) {
                                      setShowFloorPanel(false);
                                      if (accessibilityFilterButtonRef.current) {
                                        const rect = accessibilityFilterButtonRef.current.getBoundingClientRect();
                                        setAccessibilityPanelPos({
                                          top: rect.bottom + 8,
                                          right: window.innerWidth - rect.right,
                                        });
                                      }
                                      const eventName = getEventName("accessibilityGuideFilterAccessibilityOpened");
                                      AnalyticsService.trackGAEventHandler(eventName);
                                      AnalyticsService.trackMx(eventName, {});
                                    } else {
                                      setAccessibilitySearchTerm("");
                                      const eventName = getEventName("accessibilityGuideFilterAccessibilityClosed");
                                      AnalyticsService.trackGAEventHandler(eventName);
                                      AnalyticsService.trackMx(eventName, {});
                                    }
                                  }}
                                  className={`flex items-center gap-2 px-3 py-2 label-m-medium text-fg-neutral-strong 
                                    border rounded-lg transition-colors focus-blue 
                                    ${selectedAccessibilityFilters.length > 0
                                      ? "bg-bg-brand-tertiary-default border-border-token-brand-subtle hover:bg-bg-brand-tertiary-hover"
                                      : "bg-bg-brand-secondary-default border-border-token-brand-minimal hover:bg-bg-brand-secondary-hover"
                                    }`}
                                  aria-expanded={showAccessibilityPanel}
                                  aria-haspopup="dialog"
                                  aria-controls="accessibility-filters-panel"
                                  aria-label={i18n.get("filterByAccessibilityFeatures")}
                                >
                                  {i18n.get("accessibility")}
                                  {selectedAccessibilityFilters.length > 0 && (
                                    <span className="flex items-center justify-center min-w-[20px] h-5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium">
                                      {selectedAccessibilityFilters.length}
                                    </span>
                                  )}
                                  {selectedAccessibilityFilters.length == 0 && (
                                    <CaretDown
                                      size={20}
                                      className={`transition-transform ${showAccessibilityPanel ? "rotate-180" : ""}`}
                                      aria-hidden="true"
                                    />
                                  )}
                                </button>

                                {/* Accessibility Filter Panel */}
                                {showAccessibilityPanel && (
                                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                                  <div
                                    id="accessibility-filters-panel"
                                    className="fixed w-64 bg-bg-brand-secondary-default border border-neutral-100 rounded-xl shadow-lg z-50 max-h-80 flex flex-col"
                                    style={{ top: accessibilityPanelPos.top, right: accessibilityPanelPos.right }}
                                    role="dialog"
                                    aria-modal="true"
                                    aria-label="Accessibility filters"
                                    tabIndex={-1}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        setShowAccessibilityPanel(false);
                                        setAccessibilitySearchTerm("");
                                      }
                                    }}
                                  >
                                    <header className="flex items-center justify-between h-11 px-2 shrink-0">
                                      <h2 className="title-s-semibold text-fg-neutral-strong">
                                        {i18n.get("accessibility")} {i18n.get("filters")}
                                      </h2>
                                      {selectedAccessibilityFilters.length >
                                        0 && (
                                          <button
                                            onClick={() =>
                                              setSelectedAccessibilityFilters(
                                                [],
                                              )
                                            }
                                            className="label-m-medium text-fg-brand-subtle hover:text-fg-brand-strong underline underline-offset-2 px-3 py-1 focus-blue"
                                            aria-label={i18n.get("clearAllAccessibilityFilters")}
                                          >
                                            {i18n.get("clearAll")}
                                          </button>
                                        )}
                                    </header>

                                    {/* Search input */}
                                    <div className="m-2 shrink-0">
                                      <label htmlFor="accessibility-search" className="sr-only">
                                        Search accessibility features
                                      </label>
                                      <div className="flex items-center gap-4 min-h-[48px] px-4 py-2 bg-bg-brand-secondary-default border border-border-token-neutral-subtle rounded-lg">
                                        <input
                                          ref={accessibilitySearchInputRef}
                                          id="accessibility-search"
                                          type="text"
                                          placeholder={i18n.get("searchAccessibilityFeatures")}
                                          value={accessibilitySearchTerm}
                                          onChange={(e) =>
                                            setAccessibilitySearchTerm(
                                              e.target.value,
                                            )
                                          }
                                          className="flex-1 min-w-[44px] bg-transparent border-none outline-none focus:ring-0 label-m-medium text-fg-neutral-subtle placeholder:text-fg-neutral-subtle"
                                        />
                                        {accessibilitySearchTerm ? (
                                          <button
                                            onClick={() =>
                                              setAccessibilitySearchTerm("")
                                            }
                                            className="text-fg-neutral-subtle hover:text-fg-neutral-subtle p-1 flex-shrink-0"
                                            aria-label="Clear search"
                                          >
                                            <XCircle size={16} aria-hidden="true" />
                                          </button>
                                        ) : (
                                          <MagnifyingGlass
                                            size={24}
                                            className="text-fg-neutral-subtle flex-shrink-0"
                                            aria-hidden="true"
                                          />
                                        )}
                                      </div>
                                    </div>
                                    <div className="overflow-y-auto flex-1 mt-3">
                                      <div className="px-4 pb-4 space-y-2">
                                        {filteredAccessibilityOptions.length >
                                          0 ? (
                                          filteredAccessibilityOptions.map(
                                            (option) => (
                                              <Checkbox
                                                key={option.id}
                                                checked={selectedAccessibilityFilters.includes(
                                                  option.id,
                                                )}
                                                label={highlightMatches(
                                                  option.title,
                                                  accessibilitySearchTerm,
                                                )}
                                                ariaLabel={option.title}
                                                onCheck={(isChecked) => {
                                                  const eventName = getEventName("accessibilityGuideFilterAccessibilitySelected");
                                                  AnalyticsService.trackGAEventHandler(eventName);
                                                  AnalyticsService.trackMx(eventName, {
                                                    FilterId: option.id,
                                                    FilterName: option.title,
                                                    IsSelected: isChecked,
                                                  });

                                                  if (isChecked) {
                                                    setSelectedAccessibilityFilters(
                                                      [
                                                        ...selectedAccessibilityFilters,
                                                        option.id,
                                                      ],
                                                    );
                                                  } else {
                                                    setSelectedAccessibilityFilters(
                                                      selectedAccessibilityFilters.filter(
                                                        (id) =>
                                                          id !== option.id,
                                                      ),
                                                    );
                                                  }
                                                }}
                                              />
                                            ),
                                          )
                                        ) : (
                                          <div className="text-center py-8">
                                            <p className="label-m-medium text-fg-neutral-subtle">
                                              {i18n.get("noAccessibilityFeaturesFound")}
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Floor Filter Button - Only show if floor data exists */}
                              {floorFilterOptions.length > 0 && (
                                <div className="relative" data-filter-panel>
                                  <button
                                    ref={floorFilterButtonRef}
                                    onClick={() => {
                                      const opening = !showFloorPanel;
                                      setShowFloorPanel(opening);
                                      if (opening) {
                                        setShowAccessibilityPanel(false);
                                        if (floorFilterButtonRef.current) {
                                          const rect = floorFilterButtonRef.current.getBoundingClientRect();
                                          setFloorPanelPos({
                                            top: rect.bottom + 8,
                                            right: window.innerWidth - rect.right,
                                          });
                                        }
                                        const eventName = getEventName("accessibilityGuideFilterFloorOpened");
                                        AnalyticsService.trackGAEventHandler(eventName);
                                        AnalyticsService.trackMx(eventName, {});
                                      } else {
                                        setFloorSearchTerm("");
                                        const eventName = getEventName("accessibilityGuideFilterFloorClosed");
                                        AnalyticsService.trackGAEventHandler(eventName);
                                        AnalyticsService.trackMx(eventName, {});
                                      }
                                    }}

                                    className={`flex items-center gap-2 px-3 py-2 label-m-medium text-fg-neutral-strong 
                                      border rounded-lg transition-colors focus-blue 
                                      ${selectedFloorFilters.length > 0
                                        ? "bg-bg-brand-tertiary-default border-border-token-brand-subtle hover:bg-bg-brand-tertiary-hover"
                                        : "bg-bg-brand-secondary-default border-border-token-brand-minimal hover:bg-bg-brand-secondary-hover"
                                      }`}
                                                                       

                                    aria-expanded={showFloorPanel}
                                    aria-haspopup="dialog"
                                    aria-controls="floor-filters-panel"
                                    aria-label={i18n.get("filterByFloor")}
                                  >
                                    {i18n.get("floor")}
                                    {selectedFloorFilters.length > 0 && (
                                      <span className="flex items-center justify-center min-w-[20px] h-5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium">
                                        {selectedFloorFilters.length}
                                      </span>
                                    )}
                                    {selectedFloorFilters.length == 0 && (
                                      <CaretDown
                                        size={20}
                                        className={`transition-transform ${showFloorPanel ? "rotate-180" : ""}`}
                                        aria-hidden="true"
                                      />
                                    )}
                                  </button>

                                  {/* Floor Filter Panel */}
                                  {showFloorPanel && (
                                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                                    <div
                                      id="floor-filters-panel"
                                      className="fixed w-64 bg-bg-brand-secondary-default border border-neutral-100 rounded-xl shadow-lg z-50 max-h-80 flex flex-col"
                                      style={{ top: floorPanelPos.top, right: floorPanelPos.right }}
                                      role="dialog"
                                      aria-modal="true"
                                      aria-label="Floor filters"
                                      tabIndex={-1}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                          setShowFloorPanel(false);
                                          setFloorSearchTerm("");
                                        }
                                      }}
                                    >
                                      <header className="flex items-center justify-between h-11 px-2 shrink-0">
                                        <h2 className="title-s-semibold text-fg-neutral-strong">
                                          {i18n.get("floorFilters")}
                                        </h2>
                                        {selectedFloorFilters.length > 0 && (
                                          <button
                                            onClick={() =>
                                              setSelectedFloorFilters([])
                                            }
                                            className="label-m-medium text-fg-brand-subtle hover:text-fg-brand-strong underline underline-offset-2 px-3 py-1 focus-blue"
                                            aria-label={i18n.get("clearAllFloorFilters")}
                                          >
                                            {i18n.get("clearAll")}
                                          </button>
                                        )}
                                      </header>

                                      {/* Search input */}
                                      <div className="m-2 shrink-0">
                                        <label htmlFor="floor-search" className="sr-only">
                                          Search floor types
                                        </label>
                                        <div className="flex items-center gap-4 min-h-[48px] px-4 py-2 bg-bg-brand-secondary-default border border-border-token-neutral-subtle rounded-lg">
                                          <input
                                            ref={floorSearchInputRef}
                                            id="floor-search"
                                            type="text"
                                            placeholder={i18n.get("searchFloorTypes")}
                                            value={floorSearchTerm}
                                            onChange={(e) =>
                                              setFloorSearchTerm(e.target.value)
                                            }
                                            className="flex-1 min-w-[44px] bg-transparent border-none outline-none focus:ring-0 label-m-medium text-fg-neutral-subtle placeholder:text-fg-neutral-subtle"
                                          />
                                          {floorSearchTerm ? (
                                            <button
                                              onClick={() =>
                                                setFloorSearchTerm("")
                                              }
                                              className="text-fg-neutral-subtle hover:text-fg-neutral-subtle p-1 flex-shrink-0"
                                              aria-label="Clear search"
                                            >
                                              <XCircle size={16} aria-hidden="true" />
                                            </button>
                                          ) : (
                                            <MagnifyingGlass
                                              size={24}
                                              className="text-fg-neutral-subtle flex-shrink-0"
                                              aria-hidden="true"
                                            />
                                          )}
                                        </div>
                                      </div>
                                      <div className="overflow-y-auto flex-1 mt-3">
                                        <div className="px-4 pb-4 space-y-2">
                                          {filteredFloorOptions.length > 0 ? (
                                            filteredFloorOptions.map(
                                              (option) => (
                                                <Checkbox
                                                  key={option.id}
                                                  checked={selectedFloorFilters.includes(
                                                    option.id.toString(),
                                                  )}
                                                  label={highlightMatches(
                                                    option.title,
                                                    floorSearchTerm,
                                                  )}
                                                  ariaLabel={option.title}
                                                  onCheck={(isChecked) => {
                                                    const floorId = option.id.toString();
                                                    const eventName = getEventName("accessibilityGuideFilterFloorSelected");
                                                    AnalyticsService.trackGAEventHandler(eventName);
                                                    AnalyticsService.trackMx(eventName, {
                                                      FilterId: floorId,
                                                      FilterName: option.title,
                                                      IsSelected: isChecked,
                                                    });

                                                    if (isChecked) {
                                                      setSelectedFloorFilters(
                                                        [
                                                          ...selectedFloorFilters,
                                                          floorId,
                                                        ],
                                                      );
                                                    } else {
                                                      setSelectedFloorFilters(
                                                        selectedFloorFilters.filter(
                                                          (id) =>
                                                            id !== floorId,
                                                        ),
                                                      );
                                                    }
                                                  }}
                                                />
                                              ),
                                            )
                                          ) : (
                                            <div className="text-center py-8">
                                              <p className="label-m-medium text-fg-neutral-subtle">
                                                {i18n.get("noFloorTypesFound")}
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Spaces Count Section - second on mobile, first on desktop */}
                            <div className="order-2 md:order-1">

                              {/* Sidebar Toggle Button and Spaces Count - in one row on desktop */}
                              <div className="hidden md:flex items-center gap-3">
                                <button
                                  id="category-sidebar-toggle-btn"
                                  type="button"
                                  onClick={() => {
                                    const newState = !showSidebar;
                                    const eventName = getEventName("accessibilityGuideSidebarToggled");
                                    AnalyticsService.trackGAEventHandler(eventName);
                                    AnalyticsService.trackMx(eventName, { IsOpen: newState });
                                    setShowSidebar(newState);
                                  }}
                                  className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-bg-brand-secondary-hover transition-colors"
                                  aria-label={i18n.get("toggleSidebar")}
                                  aria-expanded={showSidebar}
                                >
                                  <SidebarSimple size={20} className="text-fg-neutral-strong" />
                                </button>
                                <Tooltip
                                  anchorSelect="#category-sidebar-toggle-btn"
                                  content={showSidebar ? i18n.get("hideCategories") : i18n.get("showCategories")}
                                  place="bottom"
                                  style={{ zIndex: 9999 }}
                                />

                                {/* Divider */}
                                <div className="w-px h-6 bg-border-token-neutral-minimal" aria-hidden="true" />

                                {/* Spaces Count */}
                                <h2 id="spaces-heading" className="title-s-semibold text-fg-neutral-strong">
                                  {filteredSpaces.length} {i18n.get("spaces")}
                                </h2>
                              </div>

                              {/* Spaces Count - mobile only */}
                              <h2 id="spaces-heading-mobile" className="md:hidden title-s-semibold text-fg-neutral-strong">
                                {filteredSpaces.length} {i18n.get("spaces")}
                              </h2>

                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Scrollable Spaces List */}
                      <section
                        aria-labelledby="spaces-heading spaces-heading-mobile"
                        ref={scrollContainerRef}
                        className="flex-1 overflow-y-auto scrollbar-hide hover:scrollbar-show"
                        style={{
                          scrollbarWidth: "thin",
                          scrollbarColor: "transparent transparent",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.scrollbarColor =
                            "#CBD5E1 transparent";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.scrollbarColor =
                            "transparent transparent";
                        }}
                      >
                        <div className="p-4 md:p-6">
                          {(() => {
                            // Group spaces by parent category then subcategory
                            const groupedSpaces = filteredSpaces.reduce(
                              (groups, item) => {
                                const parentName = item.parentCategoryName;
                                const subName = item.categoryName;
                                if (!groups[parentName]) {
                                  groups[parentName] = {};
                                }
                                if (!groups[parentName][subName]) {
                                  groups[parentName][subName] = [];
                                }
                                groups[parentName][subName].push(item);
                                return groups;
                              },
                              {} as Record<string, Record<string, typeof filteredSpaces>>,
                            );

                            return (
                              <>
                                {/* Grouped Spaces List */}
                                {Object.keys(groupedSpaces).length > 0 ? (
                                  <div className="space-y-10">
                                    {Object.entries(groupedSpaces).map(
                                      ([parentName, subcategories]) => (
                                        <section
                                          key={parentName}
                                          aria-labelledby={`parent-category-${parentName.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                                        >
                                          {/* Parent Category Header */}
                                          <div className="py-3 mb-2">
                                            <h2
                                              id={`parent-category-${parentName.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                                              className="title-m-bold text-fg-neutral-strong"
                                            >
                                              {parentName}
                                            </h2>
                                          </div>

                                          <div className="space-y-8">
                                            {Object.entries(subcategories).map(
                                              ([subcategoryName, subcategorySpaces]) => (
                                                <section
                                                  key={subcategoryName}
                                                  id={`category-${parentName}-${subcategoryName}`.toLowerCase().replace(/[^a-z0-9]/g, "-")}
                                                  aria-labelledby={`category-heading-${parentName}-${subcategoryName}`.toLowerCase().replace(/[^a-z0-9]/g, "-")}
                                                  className="scroll-mt-4"
                                                  ref={(el) => {
                                                    if (el) {
                                                      categorySectionRefs.current.set(subcategoryName, el);
                                                    } else {
                                                      categorySectionRefs.current.delete(subcategoryName);
                                                    }
                                                  }}
                                                >
                                                  {/* Subcategory Header */}
                                                  <h3
                                                    id={`category-heading-${parentName}-${subcategoryName}`.toLowerCase().replace(/[^a-z0-9]/g, "-")}
                                                    className="title-s-semibold md:title-s-semibold text-fg-neutral-strong mb-3 md:mb-4"
                                                  >
                                                    {subcategoryName} <span aria-hidden="true">•</span> {subcategorySpaces.length} {i18n.get("spaces")}
                                                  </h3>

                                                  {/* Spaces in this subcategory */}
                                                  <ul className="space-y-3 md:space-y-4">
                                                    {subcategorySpaces.map(
                                                      (item, index) => {
                                                        // Pre-compute tag display text (handles filter logic)
                                                        let tagsDisplayText: string;
                                                        if (
                                                          !item.space.level_option_titles ||
                                                          item.space.level_option_titles.length === 0
                                                        ) {
                                                          tagsDisplayText = `• ${i18n.get("noAccessibilityInformationAvailable")}`;
                                                        } else if (selectedAccessibilityFilters.length > 0) {
                                                          const selectedTagTitles = selectedAccessibilityFilters
                                                            .map((filterId) =>
                                                              accessibilityFilterOptions.find(
                                                                (option) => option.id === filterId,
                                                              )?.title,
                                                            )
                                                            .filter(Boolean);
                                                          const matchingTags = item.space.level_option_titles.filter(
                                                            (tag) => selectedTagTitles.includes(tag),
                                                          );
                                                          tagsDisplayText =
                                                            matchingTags.length > 0
                                                              ? matchingTags.map((tag) => `• ${tag}`).join(" ")
                                                              : `• ${i18n.get("noMatchingAccessibilityFeatures")}`;
                                                        } else {
                                                          const tagsToShow = item.space.level_option_titles.slice(0, 6);
                                                          const hasMore = item.space.level_option_titles.length > 6;
                                                          tagsDisplayText =
                                                            tagsToShow.map((tag) => `• ${tag}`).join(" ") +
                                                            (hasMore ? " ..." : "");
                                                        }

                                                        const handleSpaceClick = () => {
                                                          const eventName = getEventName("accessibilityGuideSpaceClicked");
                                                          AnalyticsService.trackGAEventHandler(eventName);
                                                          AnalyticsService.trackMx(eventName, {
                                                            SpaceName: item.space.level,
                                                            SpaceId: item.space.id,
                                                            CategoryName: item.categoryName,
                                                          });
                                                          updateSpaceUrl(item.space.id);
                                                          setTimeout(() => {
                                                            setSelectedSpace({
                                                              space: item.space,
                                                              categoryName: item.categoryName,
                                                            });
                                                            setShowSidebar(false);
                                                            const categoryId = findCategoryIdForSpace(item.categoryName);
                                                            if (categoryId) {
                                                              setSelectedCategory(categoryId);
                                                            }
                                                          }, 0);
                                                        };

                                                        const floorNames =
                                                          item.space.business_floors &&
                                                          item.space.business_floors.length > 0
                                                            ? item.space.business_floors.map((f) => f.floor_name).join(", ")
                                                            : item.space.business_floor_name;
                                                        const floorSubtitle = floorNames ? (
                                                          <span className="flex items-center gap-1 label-s-medium text-fg-neutral-subtle">
                                                            <svg
                                                              className="w-4 h-4"
                                                              fill="none"
                                                              stroke="currentColor"
                                                              strokeWidth="2"
                                                              viewBox="0 0 24 24"
                                                              aria-hidden="true"
                                                            >
                                                              <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                                              />
                                                              <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                                              />
                                                            </svg>
                                                            {floorNames}
                                                          </span>
                                                        ) : undefined;

                                                        return (
                                                          <li key={`${item.categoryId}-${item.space.id || index}`}>
                                                            <AccessibilitySpaceCard
                                                              imageMode="static"
                                                              imageSrc={
                                                                item.space.level_main_image_thumb
                                                                  ? `${bucketUrlPrefix}${item.space.level_main_image_thumb}`
                                                                  : "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=256&h=200&fit=crop&crop=center"
                                                              }
                                                              imageAlt={
                                                                item.space.level_main_image_thumb
                                                                  ? `${item.space.level} ${i18n.get("spacePreview")}`
                                                                  : i18n.get("defaultSpaceImage")
                                                              }
                                                              photoCount={item.space.level_photo_count}
                                                              onImageClick={() => {
                                                                const eventName = getEventName("accessibilityGuideSpaceImageClicked");
                                                                AnalyticsService.trackGAEventHandler(eventName);
                                                                AnalyticsService.trackMx(eventName, {
                                                                  SpaceName: item.space.level,
                                                                  SpaceId: item.space.id,
                                                                });
                                                                handleOpenSpaceImageViewer(item.space);
                                                              }}
                                                              imageAriaLabel={`${i18n.get("viewAllImagesFor")} ${item.space.level}`}
                                                              moreLabel={i18n.get("more")}
                                                              title={item.space.level}
                                                              titleClassName="title-s-semibold"
                                                              subtitle={floorSubtitle}
                                                              tagsDisplayText={tagsDisplayText}
                                                              tagsCount={item.space.level_tags_count}
                                                              onContentClick={handleSpaceClick}
                                                              contentAriaLabel={`${i18n.get("viewAll")} ${item.space.level_tags_count} ${i18n.get("accessibilityTags")} ${i18n.get("for") || "for"} ${item.space.level}`}
                                                              viewAllLabel={i18n.get("viewAll")}
                                                              accessibilityTagsLabel={i18n.get("accessibilityTags")}
                                                            />
                                                          </li>
                                                        );
                                                      },
                                                    )}
                                                  </ul>
                                                </section>
                                              ),
                                            )}
                                          </div>
                                        </section>
                                      ),
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-center py-12">
                                    <div className="flex justify-center mb-6">
                                      <img
                                        src={NoSearchResults}
                                        alt={i18n.get("noResultsFound")}
                                        className="md:max-w-[50%] py-2"
                                        aria-hidden="true"
                                      />
                                    </div>
                                    <h3 className="title-s-semibold text-gray-900 mb-2">
                                      {searchTerm
                                        ? i18n.get("noMatchingSpacesFound")
                                        : i18n.get("noSpacesAvailable")}
                                    </h3>
                                    <p className="text-gray-600">
                                      {searchTerm
                                        ? i18n.get("tryAdjustingSearchTerms")
                                        : i18n.get("noAccessibilitySpacesAvailable")}
                                    </p>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </section>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        )}

        {activeTab === "photos" && (
          <div
            role="tabpanel"
            id="photos-tabpanel"
            aria-labelledby="photos-tab"
            tabIndex={0}
            className="bg-bg-brand-secondary-default rounded-lg shadow-sm flex-1 flex flex-col min-h-0 overflow-hidden"
          >
            <AccessibilityPhotosTab
              venueDetailsResponse={data}
              expandedCategories={expandedCategories}
              setExpandedCategories={setExpandedCategories}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              showMobileMenu={showSidebar}
              setShowMobileMenu={setShowSidebar}
              pageName={isSpaceGuide ? "Accessibility Space Guide" : "Photos Tab"}
            />
          </div>
        )}

        {activeTab === "alerts" && (
          <div
            role="tabpanel"
            id="alerts-tabpanel"
            aria-labelledby="alerts-tab"
            tabIndex={0}
            className="bg-bg-brand-secondary-default rounded-lg shadow-sm flex-1 flex flex-col min-h-0 overflow-hidden"
          >
            <AccessibilityAlertsTab
              venueDetailsResponse={data}
              onRefresh={onRefresh}
              pageName={isSpaceGuide ? "Accessibility Space Guide" : "Alerts Tab"}
            />
          </div>
        )}

        {activeTab === "reviews" && (
          <div
            role="tabpanel"
            id="reviews-tabpanel"
            aria-labelledby="reviews-tab"
            tabIndex={0}
            className="bg-bg-brand-secondary-default rounded-lg shadow-sm flex-1 flex flex-col min-h-0 overflow-hidden"
          >
            <AccessibilityReviewsTab
              venueDetailsResponse={data}
              pageName={isSpaceGuide ? "Accessibility Space Guide" : "Reviews Tab"}
            />
          </div>
        )}
      </div>

      {/* Flag Photo Modal */}
      <FlagVenuePhoto
        photoReviewId={selectedImage?.review_photos_id}
        venueName={data?.details?.name}
        isOpen={isFlagVenuePhotoOpen}
        onClose={() => handleFlagVenuePhotoClose()}
        venueDetails={{
          data: {
            id: data?.details?.id,
            brandId: data?.details?.brandId,
            brandName: data?.details?.brandName,
            address: data?.details?.address,
            name: data?.details?.name,
            types: data?.details?.types,
            business_photos: data?.details?.businessPhotos,
          },
        }}
      />

      {/* Full Image Viewer */}
      {fullImageView && currentSpaceImages.length > 0 && (
        <FullImageViewer
          images={currentSpaceImages}
          currentIndex={currentImageIndex}
          onClose={() => setFullImageView(false)}
          onFlag={handleFlagPhoto}
          showFlag={true}
          showRightTab={true}
        />
      )}
    </div>
  );
}

export default AccessibilityGuideContent;
